/* eslint-env browser */
/* tslint:disable:max-line-length */

// Vendor
import { inject } from 'mobx-react';
import { AnalyticsStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { Context } from 'bernie-context';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

// Bernie
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

// Stores
import { ContextPair, MultiContextProvider, SearchCriteriaContext } from 'stores/contexts';
import { MultiItemStore } from 'stores/multi-item-store';
import { PlatformContext } from 'src/common/stores/contexts/platformContext';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

// Components
import { Footer } from '@gc/global-components';
// Controllers
import { HotelHomeController } from 'controllers/hotel/hotel-home-controller';
import { AnimatedProgressIndicator } from 'components/common/animated-progress-indicator';
import { BodyRegion } from './body-region';
import { DataDogRUM } from 'components/common/datadog-rum';
import { Header } from 'components/common/header/header';
import { InfositeController } from 'controllers/hotel/infosite-controller';
import { LodgingPwaPropertySearch } from '__generated__/api';
import { MesoScripts } from 'components/meso/internals/meso-scripts';
import { Notification } from 'components/common/notification';
import { PwaController } from 'controllers/admin/pwa-controller';
import { SearchController } from 'controllers/hotel/search-controller';
import { SearchNavigationTab } from 'components/search/search-navigation-tab';

import NavigationTabs = LodgingPwaPropertySearch.NavigationTabs;

export type MasterPageRoute = HotelHomeController | InfositeController | PwaController | SearchController;

export interface MasterPageProps extends LocalizedComponentProps, Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  className?: string;
  context?: Context;
  'data-stid'?: string;
  page?: PageStore;
  route?: MasterPageRoute;
  searchCriteria?: SearchCriteriaModel;
  multiItem?: MultiItemStore;
  pageHeader?: string;
  experiment?: ExperimentStore;
  navigationTabs?: NavigationTabs;
  headerContent: React.ReactElement<Record<string, unknown>>;
  templateContent: React.ReactElement<Record<string, unknown>>;
}

/**
 * MasterPage - stateless component for defining a reusable page structure with specific regions of content
 *
 * @param this.props {object} - the standard this.props object passed by React
 * @returns {string} - populated HTML of all applicable regions and wrapped in a div
 */
@withRouter
@withLocalization
@inject('analytics', 'context', 'experiment', 'page', 'multiItem', 'searchCriteria')
export class MasterPage extends React.Component<MasterPageProps> {
  public static defaultProps = {
    l10n: null,
    headerContent: null,
    templateContent: null,
  };
  static wrappedComponent: any;

  constructor(props) {
    super(props);

    if (this.props.analytics && this.props.route && this.props.route.routeData) {
      const routeData = this.props.route.routeData;

      this.props.analytics.setPageNamespace(routeData.rfrrNamespace || '');
      // nested the above if statement instead of appending to outer ones so we can start handling other routeData here

      if (this.props.page && !this.props.page.pageName) {
        this.props.page.pageName = routeData.pageName || 'page.unknown';
      }
    }

    this.setPageTitle = this.setPageTitle.bind(this);
  }

  public componentDidMount() {
    this.setPageTitle(this.props.page && this.props.page.title);
  }

  public componentWillUpdate(nextProps) {
    this.setPageTitle(nextProps.page && nextProps.page.title);
  }

  public setPageTitle(title) {
    document.title = title || this.props.l10n.formatText('common.defaultPageTitle');
  }

  public render() {
    const { context, 'data-stid': dataStid, searchCriteria, pageHeader, navigationTabs } = this.props;

    const headerDefault = <Header searchCriteria={searchCriteria} />;

    const headerContentRegion = this.props.headerContent && <header>{this.props.headerContent}</header>;

    const contextPairs: ContextPair[] = [
      { context: PlatformContext, value: context },
      { context: SearchCriteriaContext, value: searchCriteria },
    ];

    const pageContainerRegionProps = {
      'data-stid': dataStid,
      headerContentRegion,
      headerDefault,
      pageHeader,
      navigationTabs,
      templateContent: this.props.templateContent,
    };

    return (
      <MultiContextProvider contextPairs={contextPairs}>
        <ErrorBoundary label="hotel.masterPage.renderToNodeStreamTest">
          <Experiment name="PWA_RENDER_TO_NODE_STREAM">
            <ExperimentControl />
            <ExperimentVariant bucket={1} />
          </Experiment>
        </ErrorBoundary>
        <PageContainerRegion {...pageContainerRegionProps} />
        <ErrorBoundary label="meso.scripts.errorboundary">
          <MesoScripts />
        </ErrorBoundary>
      </MultiContextProvider>
    );
  }
}

const PageContainerRegion = ({
  'data-stid': dataStid,
  headerContentRegion,
  headerDefault,
  pageHeader,
  navigationTabs,
  templateContent,
}) => (
  <div data-stid={dataStid}>
    <DataDogRUM />
    <Experiment name="Lodging_Shopping_PWA_EGTNL_AA_Test">
      <ExperimentControl />
      <ExperimentVariant bucket={1} />
    </Experiment>
    <ErrorBoundary label="hotel.masterPage.header">{headerContentRegion || headerDefault}</ErrorBoundary>
    <main>
      {navigationTabs && <SearchNavigationTab navigationBarTabList={navigationTabs} />}
      <BodyRegion templateContent={templateContent} pageHeader={pageHeader} />
    </main>
    <Footer />
    <AnimatedProgressIndicator />
    <Notification />
  </div>
);
